import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { colors } from '../constants';

/** Minimal CSV parser supporting quoted fields and commas within quotes. */
function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[i + 1] === '\n') i += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

/**
 * Parses a CSV file of purchase-invoice line items and returns validated rows.
 * Required columns: sku, name, quantity, unitCost, sellingPrice.
 */
export default function FileImportModal({ open, onClose, onImport }) {
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState(null);
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);

  const reset = () => {
    setFileName(null);
    setRows([]);
    setErrors([]);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    const text = await file.text();
    const grid = parseCsv(text);
    if (grid.length < 2) {
      setErrors([{ row: 0, message: 'File has no data rows' }]);
      setRows([]);
      return;
    }
    const headers = grid[0].map((h) => h.trim().toLowerCase());
    const idx = (name) => headers.indexOf(name);
    const parsed = [];
    const rowErrors = [];

    for (let i = 1; i < grid.length; i += 1) {
      const raw = grid[i];
      const get = (...names) => {
        for (const n of names) {
          const c = idx(n);
          if (c >= 0 && raw[c] != null) return raw[c].trim();
        }
        return '';
      };
      const rowNum = i + 1;
      const sku = get('sku');
      const name = get('name');
      if (!sku && !name) {
        rowErrors.push({ row: rowNum, message: 'SKU or name is required' });
        continue;
      }
      const qty = parseFloat(get('quantity', 'qty'));
      if (!Number.isFinite(qty) || qty <= 0) {
        rowErrors.push({ row: rowNum, message: 'Invalid quantity' });
        continue;
      }
      const cost = parseFloat(get('unitcost', 'cost', 'price'));
      if (!Number.isFinite(cost) || cost < 0) {
        rowErrors.push({ row: rowNum, message: 'Invalid unit cost' });
        continue;
      }
      const type = (get('producttype', 'type') || 'product').toLowerCase();
      // Required MMS selling price. Distinct from unit cost; the legacy `price`
      // column remains an alias for unit cost, so selling price uses its own
      // `sellingPrice` column.
      const rawSellingPrice = get('sellingprice');
      if (!rawSellingPrice) {
        rowErrors.push({ row: rowNum, message: 'Selling price is required' });
        continue;
      }
      const parsedSellingPrice = parseFloat(rawSellingPrice);
      if (!Number.isFinite(parsedSellingPrice) || parsedSellingPrice < 0) {
        rowErrors.push({ row: rowNum, message: 'Invalid selling price' });
        continue;
      }
      const sellingPrice = String(parsedSellingPrice);
      parsed.push({
        sku,
        name,
        productType: type === 'ingredient' ? 'ingredient' : 'product',
        quantity: String(qty),
        unitCost: String(cost),
        sellingPrice,
        unit: get('unit') || undefined,
        barcode: get('barcode') || undefined,
        expiryDate: get('expirydate', 'expiry') || undefined,
      });
    }
    setRows(parsed);
    setErrors(rowErrors);
  };

  const handleConfirm = () => {
    if (rows.length === 0) return;
    onImport(rows);
    reset();
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        Import line items from CSV
        <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 12, top: 12 }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        {!fileName ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <UploadFileIcon sx={{ fontSize: 48, color: colors.gray400 }} />
            <Typography sx={{ fontWeight: 600, mt: 1 }}>Select a CSV file</Typography>
            <Typography variant="body2" sx={{ color: colors.gray500, mt: 0.5, mb: 2 }}>
              Required columns: sku, name, quantity, unitCost, sellingPrice.
              Optional: productType, unit, barcode, expiryDate
            </Typography>
            <Button
              variant="contained"
              onClick={() => inputRef.current?.click()}
              sx={{ backgroundColor: colors.primary, textTransform: 'none' }}
            >
              Choose File
            </Button>
          </Box>
        ) : (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {fileName}
              </Typography>
              <Chip size="small" color="success" label={`${rows.length} valid rows`} />
              {errors.length > 0 && (
                <Chip size="small" color="error" label={`${errors.length} errors`} />
              )}
              <Button size="small" onClick={() => inputRef.current?.click()} sx={{ textTransform: 'none' }}>
                Choose different file
              </Button>
            </Stack>

            {errors.length > 0 && (
              <Box sx={{ mb: 2 }}>
                {errors.slice(0, 5).map((e, i) => (
                  <Typography key={i} variant="caption" sx={{ color: colors.error, display: 'block' }}>
                    Row {e.row}: {e.message}
                  </Typography>
                ))}
                {errors.length > 5 && (
                  <Typography variant="caption" sx={{ color: colors.error }}>
                    ...and {errors.length - 5} more
                  </Typography>
                )}
              </Box>
            )}

            {rows.length > 0 && (
              <Box sx={{ maxHeight: 280, overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>SKU</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Unit Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.slice(0, 50).map((r, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ color: colors.primary }}>{r.sku || '(no sku)'}</TableCell>
                        <TableCell>{r.name}</TableCell>
                        <TableCell align="right">{r.quantity}</TableCell>
                        <TableCell align="right">${r.unitCost}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={rows.length === 0}
          onClick={handleConfirm}
          sx={{ backgroundColor: colors.primary, textTransform: 'none', fontWeight: 600 }}
        >
          Import {rows.length} items
        </Button>
      </DialogActions>
    </Dialog>
  );
}
